import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import {
  Result, Space, Typography,
} from 'antd';
import { useParams } from 'react-router-dom';
import { captureException } from '@sentry/react';
import { CloudUploadOutlined, DatabaseOutlined, EditOutlined } from '@ant-design/icons';
import useHttpClient from '/src/hooks/useHttpClient';
import { AuthContext, UploadContext } from '/src/constants/contexts';
import Router from '/src/constants/router';
import { EMPTY_OBJECT, UPLOAD_API_BASE_URL } from '/src/constants';
import UploadProvider from '/src/components/UploadProvider';
import UploadQuota from '/src/components/UploadQuota';
import useUploadQuota from '/src/hooks/useUploadQuota';
import Card from '/src/components/Card';
import VideoForm from './Form';
import Header from './Header';
import PublishButton from './PublishButton';

function MobilePublishAction({ onChange }) {
  const actionRef = useRef(null);
  const [formActionVisible, setFormActionVisible] = useState(false);

  useEffect(() => {
    if (!actionRef.current) {
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      setFormActionVisible(entry.isIntersecting);
    }, { threshold: 0.1 });
    observer.observe(actionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div ref={actionRef} className="video-publish-mobile md:hidden">
        <PublishButton onChange={onChange} />
      </div>
      {!formActionVisible && (
        <div className="video-publish-mobile-fixed md:hidden">
          <PublishButton onChange={onChange} />
        </div>
      )}
    </>
  );
}

function EditVideo() {
  const httpClient = useHttpClient({ baseURL: UPLOAD_API_BASE_URL });
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const { video, setVideo, status } = useContext(UploadContext);
  const { quota } = useUploadQuota();
  const saveFormRef = useRef(null);
  const uploadDisabled = Boolean(quota?.disabled);
  const isDisabled = video?.state === 'PUBLISHED' ||
    video?.state === 'PUBLISHING' ||
    (
      video?.fileState === 'UPLOADED' && video?.state === 'WAITING_FOR_PUBLISH'
    ) || video?.fileState === 'REMOVED';
  const onVideoUpdateRes = useCallback(
    (res) => {
      if (!res) {
        return;
      }
      // console.log(res);
      const { data: { data } = EMPTY_OBJECT } = res;
      setVideo(data);
    },
    [],
  );
  const handlePublishChange = (...args) => {
    onVideoUpdateRes(...args);
    saveFormRef.current?.();
  };

  useEffect(() => {
    if (!id || !user) {
      return;
    }
    function fetchVideo() {
      httpClient.get(`/videos/${id}`)
        .then(onVideoUpdateRes)
        .catch((e) => {
          if (e.response?.status === 404) {
            Router.navigate('/videos');
          }
          captureException(e);
          console.log(e);
        });
    }
    fetchVideo();
    if (!video || !['WAITING_FOR_PUBLISH', 'PUBLISHING'].includes(video.state) || video.fileState !== 'UPLOADED') {
      return;
    }
    const interval = setInterval(fetchVideo, 5000);
    return () => clearInterval(interval);
  }, [user, video?.state, video?.fileState]);

  if (!video) {
    return null;
  }
  if (status === 'none_operational') {
    return (
      <Result
        status="warning"
        icon={<DatabaseOutlined />}
        title={(
          <>
            <Typography.Title level={5}>سرویس آپلود جهت بروز رسانی فعلا در دسترس نیست!</Typography.Title>
            <Typography.Paragraph> به زودی برمیگردیم :)</Typography.Paragraph>
          </>
        )}
      />
    );
  }
  return (
    <main className="video-edit-page mx-auto max-w-5xl">
      <Header
        video={video}
        className="mb-4"
        actions={(
          <div className="hidden md:block">
            <PublishButton onChange={handlePublishChange} />
          </div>
        )}
      />
      <Space orientation="vertical" size="middle" className="w-full">
        {/* <UploadQuota /> */}
        {!uploadDisabled && (
          <Card title="فایل ویدیو" Icon={CloudUploadOutlined}>
            <UploadProvider.UploadBox />
          </Card>
        )}
        <Card title="اطلاعات ویدیو" Icon={EditOutlined}>
          <VideoForm
            video={video}
            onChange={onVideoUpdateRes}
            disabled={isDisabled}
            saveFormRef={saveFormRef}
          />
        </Card>
        <MobilePublishAction onChange={handlePublishChange} />
      </Space>
    </main>

  );
}
export default EditVideo;
